import { useEffect, useRef } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Branded confirmation modal — replaces every `window.confirm()` in the app
 * so the user never sees the browser-native dialog (with its embedded URL).
 *
 * Usage :
 *   <ConfirmDialog
 *     open={state}
 *     title="Fermer cette file ?"
 *     message={<>Cette action est <b>irréversible</b>.</>}
 *     confirmLabel="OK"
 *     cancelLabel="Annuler"
 *     onConfirm={handle}
 *     onCancel={() => setState(false)}
 *     destructive
 *   />
 */
export default function ConfirmDialog({
  open,
  title = "Confirmer",
  message,
  confirmLabel = "OK",
  cancelLabel = "Annuler",
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
  testid = "confirm-dialog",
}) {
  const confirmRef = useRef(null);

  // Focus the primary button on open + close on Escape (kept identical to
  // the previous one-off modals so behaviour is unchanged).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => confirmRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open, busy, onCancel]);

  if (!open) return null;

  const accent = destructive ? "destructive" : "ink";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
      onClick={() => !busy && onCancel?.()}
      data-testid={`${testid}-backdrop`}
    >
      <div
        className={`bg-white border-2 ${destructive ? "border-destructive" : "border-ink"} max-w-md w-full rounded-xl overflow-hidden shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-labelledby={`${testid}-title`}
        data-testid={testid}
      >
        <div className={`px-5 py-4 border-b border-border bg-${accent}/5 flex items-center gap-3`}>
          {destructive && <AlertTriangle size={22} className="text-destructive shrink-0" />}
          <h3 id={`${testid}-title`} className="font-display text-lg font-black text-ink">
            {title}
          </h3>
        </div>
        <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
          {typeof message === "string" ? <p>{message}</p> : message}
        </div>
        <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap justify-end gap-2">
          <button
            disabled={busy}
            onClick={onCancel}
            className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-ink text-ink text-xs font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid={`${testid}-cancel-btn`}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            disabled={busy}
            onClick={onConfirm}
            className={`inline-flex items-center justify-center h-9 px-5 rounded-md text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50 ${destructive ? "bg-destructive hover:bg-destructive/90" : "bg-ink hover:bg-ink/90"}`}
            data-testid={`${testid}-confirm-btn`}
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
